/*
  FS custom: product photo clean-up, done in the browser so nobody has to re-edit images.

  1. Grey studio backgrounds: some photos sit on light grey (e.g. #f2f2f2) instead of pure white,
     so the "multiply into the page color" trick leaves a grey box. If the corners are an even
     light grey (232–252) the image is brightened just enough to turn that grey white.

  2. Extra empty space: some photos have the product small in the middle of a big empty canvas,
     so they look smaller than photos where the product fills the frame. We find the product's
     bounding box (pixels that differ from the background colour) and scale/shift the image so the
     product fills ~90% of its frame (max 1.8x, to avoid blur). Uses the CSS `scale` / `translate`
     properties so the theme's own transforms keep working.

  Photos without a plain or transparent background (lifestyle shots etc.) are left alone.
  Works on a tiny 160px copy of each image (same-origin, so canvas can read it).
*/
(function () {
  var WHITEN = [
    '.product__media-image-wrapper img',
    '.product__thumbnail img',
    '.product-item__primary-image',
    '.product-item__secondary-image',
    '.line-item__image',
    '.fs-deal__image img',
    '.fs-search-card img',
    '.quick-buy-product__image',
    '.product-sticky-form__image',
    '.fs-oc__image img'
  ].join(',');
  // Only these get the "fill the frame" treatment (their frames clip overflow)
  var TRIM = [
    '.product__media-image-wrapper img',
    '.product-item__primary-image',
    '.line-item__image',
    '.fs-deal__image img',
    '.product-sticky-form__image',
    '.fs-oc__image img'
  ].join(',');
  var FILL = 0.9, MAX_SCALE = 1.8, SAMPLE = 160;
  var cache = {};
  var trimmed = [];

  function sampleUrl(src) {
    var url;
    try { url = new URL(src, location.href); } catch (e) { return null; }
    url.searchParams.set('width', String(SAMPLE));
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    return url.toString();
  }

  function analyse(probe) {
    var w = probe.naturalWidth, h = probe.naturalHeight;
    if (!w || !h) return null;
    var canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(probe, 0, 0);
    var data = ctx.getImageData(0, 0, w, h).data;
    function px(x, y) { var i = (y * w + x) * 4; return [data[i], data[i + 1], data[i + 2], data[i + 3]]; }

    var corners = [px(1, 1), px(w - 2, 1), px(1, h - 2), px(w - 2, h - 2)];
    var transparent = corners.every(function (c) { return c[3] < 20; });
    var result = { factor: 0, box: null };

    var bg = null;
    if (!transparent) {
      if (corners.some(function (c) { return c[3] < 250; })) return result;
      var avg = [0, 0, 0];
      corners.forEach(function (c) { avg[0] += c[0] / 4; avg[1] += c[1] / 4; avg[2] += c[2] / 4; });
      var uniform = corners.every(function (c) {
        return Math.abs(c[0] - avg[0]) < 10 && Math.abs(c[1] - avg[1]) < 10 && Math.abs(c[2] - avg[2]) < 10;
      });
      if (!uniform) return result; // lifestyle / busy background — leave it
      bg = avg;
      var grey = (avg[0] + avg[1] + avg[2]) / 3;
      var spread = Math.max(avg[0], avg[1], avg[2]) - Math.min(avg[0], avg[1], avg[2]);
      if (spread <= 6 && grey >= 232 && grey <= 252) result.factor = Math.min(255 / grey, 1.1);
    }

    // Bounding box of the product
    var minX = w, minY = h, maxX = -1, maxY = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4, isContent;
        if (transparent) {
          isContent = data[i + 3] > 24;
        } else {
          isContent = data[i + 3] > 24 && (Math.abs(data[i] - bg[0]) > 24 || Math.abs(data[i + 1] - bg[1]) > 24 || Math.abs(data[i + 2] - bg[2]) > 24);
        }
        if (isContent) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return result;
    var box = { x0: minX / w, y0: minY / h, x1: (maxX + 1) / w, y1: (maxY + 1) / h, ratio: w / h };
    if ((box.x1 - box.x0) < 0.97 || (box.y1 - box.y0) < 0.97) result.box = box;
    return result;
  }

  function measure(src, done) {
    var key = (src || '').split('?')[0];
    var entry = cache[key];
    if (entry && entry.waiting) { entry.waiting.push(done); return; }
    if (entry) { done(entry.result); return; }
    cache[key] = { waiting: [done] };
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    var finish = function (res) {
      var waiting = cache[key].waiting;
      cache[key] = { result: res };
      waiting.forEach(function (fn) { fn(res); });
    };
    probe.onload = function () {
      try { finish(analyse(probe)); } catch (e) { finish(null); }
    };
    probe.onerror = function () { finish(null); };
    var url = sampleUrl(src);
    if (!url) return finish(null);
    probe.src = url;
  }

  // Scale/shift an image so its product fills the frame
  function fit(img) {
    var box = img.__fsBox;
    if (!box) return;
    img.style.scale = '';
    img.style.translate = '';
    var W = img.clientWidth, H = img.clientHeight;
    if (!W || !H) return;
    var fitMode = getComputedStyle(img).objectFit;
    var rw = W, rh = H;
    if (fitMode === 'contain' || fitMode === 'scale-down') {
      if (W / H > box.ratio) { rh = H; rw = H * box.ratio; } else { rw = W; rh = W / box.ratio; }
    } else if (fitMode === 'cover') {
      return; // already fills its frame
    }
    var bw = (box.x1 - box.x0) * rw, bh = (box.y1 - box.y0) * rh;
    if (!bw || !bh) return;
    var s = Math.min(FILL * W / bw, FILL * H / bh, MAX_SCALE);
    if (s < 1.04) return;
    var dx = ((box.x0 + box.x1) / 2 - 0.5) * rw;
    var dy = ((box.y0 + box.y1) / 2 - 0.5) * rh;
    // Account for any translate already in the theme's transform (e.g. translate(-50%,-50%))
    var e = 0, f = 0;
    var t = getComputedStyle(img).transform;
    if (t && t !== 'none') {
      var m = t.match(/matrix\(([^)]+)\)/);
      if (m) { var p = m[1].split(',').map(parseFloat); e = p[4] || 0; f = p[5] || 0; }
    }
    var tx = -s * dx + (1 - s) * e;
    var ty = -s * dy + (1 - s) * f;
    img.style.scale = s.toFixed(3);
    img.style.translate = tx.toFixed(1) + 'px ' + ty.toFixed(1) + 'px';
  }

  // FS: main product photos stay invisible until they're brightened/scaled, then fade in (no visible
  // zoom "pop"). CSS in assets/custom-styles.css ("FS custom: calm product page load").
  function ready(img) { img.setAttribute('data-fs-ready', ''); }

  function apply(img) {
    if (img.__fsWhiten) return;
    var src = img.currentSrc || img.src;
    if (!src || src.indexOf('data:') === 0) { ready(img); return; }
    img.__fsWhiten = true;
    var trim = img.matches(TRIM);
    measure(src, function (res) {
      if (!res) { ready(img); return; }
      if (res.factor > 1.001) img.style.filter = 'brightness(' + res.factor.toFixed(3) + ')';
      if (trim && res.box) {
        img.__fsBox = res.box;
        trimmed.push(img);
        if (img.complete) { fit(img); ready(img); }
        else img.addEventListener('load', function () { fit(img); ready(img); }, { once: true });
      } else {
        ready(img);
      }
    });
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(WHITEN)) apply(root);
    Array.prototype.forEach.call(root.querySelectorAll(WHITEN), apply);
  }

  var resizeTimer;
  function refit() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      trimmed = trimmed.filter(function (img) { return img.isConnected; });
      trimmed.forEach(fit);
    }, 150);
  }

  function start() {
    scan(document);
    window.addEventListener('resize', refit);
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          Array.prototype.forEach.call(m.addedNodes, function (n) { if (n.nodeType === 1) scan(n); });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
    // Drawers / sliders that were hidden at load get real sizes later
    document.addEventListener('click', function () { setTimeout(refit, 400); }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
